import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { effectiveOrgId, type OrgTreeNode, type ScopeVerdict } from "@/lib/org-structure-tree";

/** O que o diálogo mostra de uma unidade: serve o nó da árvore e o resumo da prévia. */
export type ScopeUnit = Pick<OrgTreeNode, "key" | "id" | "targetId" | "name" | "levelName">;

/**
 * Avisa o efeito colateral de aprovar/tirar do escopo antes de gravar.
 *
 * Aprovar sobe (no CBC não existe cadastrar filho sem pai); tirar do escopo
 * desce (sem o pai, os filhos ficam incadastráveis). Sem efeito colateral,
 * `previewCascade` devolve lista vazia e o componente não renderiza nada —
 * perguntar sem ter o que informar é fricção à toa.
 */
export function ScopeVerdictDialog({
  open,
  node,
  affected,
  verdict,
  selectedCount = 1,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  node: ScopeUnit | null;
  /**
   * Quem MAIS será afetado, já calculado por quem abriu o diálogo
   * (`previewCascade`). Receber pronto em vez de recalcular evita percorrer a
   * floresta canônica duas vezes por clique e mantém a decisão de "abrir ou
   * gravar direto" num lugar só.
   */
  affected: ScopeUnit[];
  verdict: ScopeVerdict | null;
  selectedCount?: number;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  if (node === null || verdict === null || affected.length === 0) return <></>;

  const isApprove = verdict === "approved";

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {selectedCount > 1 ? (verdict === "pending" ? t("Return {{count}} selected items to pending?", { count: selectedCount }) : isApprove ? t("Approve {{count}} selected items?", { count: selectedCount }) : t("Take {{count}} selected items out of scope?", { count: selectedCount })) : verdict === "pending" ? t("Return to pending") : isApprove
              ? t("Approve {{unit}}?", { unit: effectiveOrgId(node) })
              : t("Take {{unit}} out of scope?", { unit: effectiveOrgId(node) })}
          </DialogTitle>
          <DialogDescription>
            {selectedCount > 1 && <span>{t("Changing {{selected}} selected items also affects {{count}} other items.", { selected: selectedCount, count: affected.length })} </span>}
            {selectedCount > 1 ? (verdict === "pending" ? t("Related units with the same decision will also return to pending.") : null) : verdict === "pending" ? t("Related units with the same decision will also return to pending.") : isApprove
              ? t(
                  "To register {{unit}}, {{count}} unit(s) above it will also be approved — the CBC cannot register a child without its parent.",
                  { unit: effectiveOrgId(node), count: affected.length },
                )
              : t(
                  "{{count}} unit(s) below {{unit}} will also leave the scope, because without it they cannot be registered.",
                  { unit: effectiveOrgId(node), count: affected.length },
                )}
          </DialogDescription>
        </DialogHeader>

        <ul className="max-h-48 overflow-y-auto text-sm">
          {affected.map((unit) => (
            <li key={unit.key} className="py-0.5">
              {unit.levelName}: {effectiveOrgId(unit)}
              {unit.name ? ` — ${unit.name}` : null}
            </li>
          ))}
        </ul>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            {t("Cancel")}
          </Button>
          <Button onClick={onConfirm}>{t("Confirm")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
